/** Parses and renders the columns and ordering hints carried by Core DataView metadata. */
import {RangeListColumn} from "../rangelist/RangeListTypes";

/** How a view column is rendered. Mirrors the producer's contract in the statistics service. */
export enum DataViewColumnType {
   TEXT = "TEXT",
   COUNT = "COUNT",
   DAYS = "DAYS"
}

export interface DataViewColumn {
   key: string;
   label: string;
   dataType: DataViewColumnType;
}

export type DataViewRowValues = Record<string, string | number | null>;

export type DataViewSortDirection = "ASC" | "DESC";

export interface DataViewDefaultSort {
   key: string;
   direction: DataViewSortDirection;
}

export interface DataViewAnnotations {
   columns: DataViewColumn[];
   rows: Record<string, DataViewRowValues>;
   defaultSort?: DataViewDefaultSort;
}

/** Column width per data type, in the same relative units the stock list's own columns use. */
const COLUMN_WIDTHS: Record<DataViewColumnType, number> = {
   [DataViewColumnType.TEXT]: 14,
   [DataViewColumnType.COUNT]: 8,
   [DataViewColumnType.DAYS]: 8
};

/** True when a parsed JSON entry has the key, label and known data type a view column needs. */
const isDataViewColumn = (candidate: unknown): candidate is DataViewColumn => {
   if (!candidate || typeof candidate !== "object") {
      return false;
   }
   const column = candidate as Partial<DataViewColumn>;
   return typeof column.key === "string"
      && typeof column.label === "string"
      && Object.values(DataViewColumnType).includes(column.dataType as DataViewColumnType);
};

/** The generated `metadata` scalar is untyped JSON; this is the only place that inspects it. */
export const parseDataViewAnnotations = (metadata: unknown): DataViewAnnotations | null => {
   if (!metadata || typeof metadata !== "object") {
      return null;
   }
   const candidate = metadata as {columns?: unknown, rows?: unknown, defaultSort?: unknown};
   if (!Array.isArray(candidate.columns)) {
      return null;
   }
   const columns = candidate.columns.filter(isDataViewColumn);
   if (columns.length === 0) {
      return null;
   }
   const hasRows = !!candidate.rows && typeof candidate.rows === "object" && !Array.isArray(candidate.rows);
   const sortCandidate = candidate.defaultSort && typeof candidate.defaultSort === "object"
      ? candidate.defaultSort as Partial<DataViewDefaultSort>
      : undefined;
   const defaultSort = sortCandidate
      && typeof sortCandidate.key === "string"
      && columns.some(column => column.key === sortCandidate.key)
      && (sortCandidate.direction === "ASC" || sortCandidate.direction === "DESC")
      ? {key: sortCandidate.key, direction: sortCandidate.direction}
      : undefined;
   return {
      columns,
      rows: hasRows ? candidate.rows as Record<string, DataViewRowValues> : {},
      ...(defaultSort ? {defaultSort} : {})
   };
};

/** Formats one cell: counts with Danish grouping, days with a "d" suffix, text as is, missing as blank. */
export const formatDataViewValue = (value: string | number | null | undefined, dataType: DataViewColumnType): string => {
   if (value === null || value === undefined) {
      return '';
   }
   switch (dataType) {
      case DataViewColumnType.COUNT:
         return typeof value === "number"
            ? value.toLocaleString("da-DK", {maximumFractionDigits: 0})
            : String(value);
      case DataViewColumnType.DAYS:
         return `${value}d`;
      default:
         return String(value);
   }
};

/** Builds range-list columns for the view's annotations, ids prefixed `view:` so they never collide with the list's own. */
export const dataViewRangeColumns = <T extends {_id: string}>(annotations: DataViewAnnotations): RangeListColumn<T>[] =>
   annotations.columns.map((column): RangeListColumn<T> => ({
      id: `view:${column.key}`,
      label: column.label,
      width: COLUMN_WIDTHS[column.dataType],
      align: column.dataType === DataViewColumnType.TEXT ? 'left' : 'right',
      get: (row: T) => annotations.rows[row._id]?.[column.key],
      render: (value: string | number | null | undefined) => formatDataViewValue(value, column.dataType)
   }));

/** The ids `dataViewRangeColumns` would produce, for tab include-lists. */
export const dataViewColumnIds = (annotations: DataViewAnnotations): string[] =>
   annotations.columns.map(column => `view:${column.key}`);
